// src/components/admin/InviteMembersForm.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../ui/card';
import api from '../../utils/api';
import { showToast } from '../../utils/toast';

/**
 * InviteMembersForm component provides a form to invite new members to the organization
 * 
 * @param {Function} onMembersInvited - Callback function called after successful invitation
 * @returns {JSX.Element} Rendered component
 */
const InviteMembersForm = ({ onMembersInvited }) => {
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviting, setInviting] = useState(false);
  
  /**
   * Parse email string into array of valid emails
   * @param {string} emailString - String containing emails separated by commas, semicolons, or newlines
   * @returns {string[]} Array of trimmed, valid email addresses
   */
  const parseEmails = (emailString) => {
    return emailString
      .split(/[,;\n]/)
      .map(email => email.trim())
      .filter(email => email && validateEmail(email));
  };
  
  /**
   * Basic email validation
   * @param {string} email - Email to validate
   * @returns {boolean} True if email format is valid
   */
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  
  /**
   * Handle form submission
   * @param {Event} e - Form submit event
   */
  const handleInviteMembers = async (e) => {
    e.preventDefault();
    
    // Parse and validate emails
    const emails = parseEmails(inviteEmails);
    
    if (emails.length === 0) {
      showToast.error('Please enter at least one valid email address');
      return;
    }
    
    setInviting(true);
    
    try {
      const response = await api.post('/organizations/invite', { emails });
      showToast.success('Invitations sent successfully');
      setInviteEmails(''); // Clear the input
      
      // Call the callback with the results
      if (onMembersInvited && typeof onMembersInvited === 'function') {
        onMembersInvited(response.data.results);
      }
    } catch (error) {
      console.error('Error inviting members:', error);
      showToast.error(error.response?.data?.message || 'Failed to send invitations');
    } finally {
      setInviting(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite Team Members</CardTitle>
        <CardDescription>
          Enter email addresses separated by commas, semicolons, or new lines
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleInviteMembers}>
          <div className="mb-4">
            <textarea
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              rows={4}
              placeholder="john@example.com, amy@example.com"
            ></textarea>
            <div className="mt-1 text-xs text-gray-500">
              Invalid emails will be skipped. Ensure all addresses are formatted correctly.
            </div>
          </div>
          <button
            type="submit"
            disabled={inviting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {inviting ? 'Sending Invites...' : 'Send Invites'}
          </button>
        </form>
      </CardContent>
    </Card>
  );
};

InviteMembersForm.propTypes = {
  onMembersInvited: PropTypes.func
};

export default InviteMembersForm;